import React, { Fragment, useMemo } from 'react';
import './Drawer.css';
import { Dialog, Transition } from '@headlessui/react';
import { CloseIconButton, Label } from '@nooz-optics/nooz-components-lib';
import { useDrawer } from '../../../../utils/recoil/drawer';
import { Button } from '../../Elements';
import clsx from 'clsx';

export const Drawer = () => {
  const drawer = useDrawer();
  const drawerRect = useMemo(() => {
    let transition;
    switch (drawer.value.placement) {
      case 'left':
        transition = {
          enterFrom: '-translate-x-full',
          enterTo: 'translate-x-0',
          leaveFrom: 'translate-x-0',
          leaveTo: '-translate-x-full',
        };
        break;
      case 'bottom':
        transition = {
          enterFrom: 'translate-y-full',
          enterTo: 'translate-y-0',
          leaveFrom: 'translate-y-0',
          leaveTo: 'translate-y-full',
        };
        break;
      case 'top':
        transition = {
          enterFrom: '-translate-y-full',
          enterTo: 'translate-y-0',
          leaveFrom: 'translate-y-0',
          leaveTo: '-translate-y-full',
        };
        break;
      case 'right':
      default:
        transition = {
          enterFrom: 'translate-x-full',
          enterTo: 'translate-x-0',
          leaveFrom: 'translate-x-0',
          leaveTo: 'translate-x-full',
        };
        break;
    }
    return {
      transition,
      height:
        (drawer.value.placement === 'left' ||
          drawer.value.placement === 'right') &&
        'inset-y-0',
      width:
        drawer.value.placement === 'top' || drawer.value.placement === 'bottom'
          ? 'w-full'
          : drawer.value.width || '',
    };
  }, [drawer.value]);

  return (
    <Transition
      show={drawer.value.show}
      as={Fragment}>
      <Dialog
        as='div'
        unmount={false}
        open={drawer.value.modal}
        onClose={drawer.close}
        className='fixed inset-0 overflow-hidden z-50'>
        <div className='absolute inset-0 overflow-hidden'>
          {drawer.value.backdropLink && (
            <Transition.Child
              as={Fragment}
              enter='ease-in-out duration-100'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in-out duration-100'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'>
              <div className='hidden xl:block absolute left-1/4 top-1/2 pointer-events-none z-50'>
                <Label
                  text={drawer.value.backdropLink?.text}
                  color={{ classname: 'primary' }}
                  size='lg'
                  decoration='underline'
                />
              </div>
            </Transition.Child>
          )}
          <Transition.Child
            as={Fragment}
            enter={`transition ease-in-out duration-${drawer.value.animationDuration} transform`}
            enterFrom='opacity-0'
            enterTo='opacity-100'
            entered='opacity-100'
            leave={`transition ease-in-out duration-${drawer.value.animationDuration} transform`}
            leaveFrom='opacity-100'
            leaveTo='opacity-0'>
            <Dialog.Overlay
              className={clsx(
                'fixed',
                'inset-0',
                'cursor-pointer',
                'bg-gray-300',
                'bg-opacity-75',
                'backdrop-filter',
                'backdrop-blur-sm',
                'transition-opacity'
              )}
            />
          </Transition.Child>

          <div
            className={`fixed ${drawerRect.height} ${drawer.value.placement}-0 max-w-full max-h-full flex`}>
            <Transition.Child
              as={Fragment}
              enter={`transition ease-in-out duration-${drawer.value.animationDuration} transform`}
              leave={`transition ease-in-out duration-${drawer.value.animationDuration} transform`}
              {...drawerRect.transition}>
              <div
                className={`bg-white flex ${drawerRect.width} flex-col h-full justify-between`}>
                <div className='flex flex-col h-full overflow-auto grow-0 no-scrollbar'>
                  <div className='flex flex-shrink-0 w-full items-center justify-between px-6 py-3'>
                    {/* Close button */}
                    <CloseIconButton onClick={drawer.close} />
                    {/* Title */}
                    {drawer.value.title && (
                      <div className='pl-3'>
                        <Label
                          {...drawer.value.title}
                          color={{ classname: 'gray-800' }}
                          size='2xl'
                          weight='bold'
                        />
                      </div>
                    )}
                    {/* Action button */}
                    {drawer.value.actionButton}
                  </div>
                  {/* Sub Header */}
                  {drawer.value.subHeader && drawer.value.subHeader}
                  {/* Body */}
                  {drawer.value.body && (
                    <div className='overflow-y-auto grow-0 flex items-start no-scrollbar'>
                      {drawer.value.body}{' '}
                    </div>
                  )}
                </div>
                {/* Footer */}
                {drawer.value.footer && (
                  <div className='flex-shrink-0'>{drawer.value.footer}</div>
                )}
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const DrawerFloatingCloseButton = () => {
  const drawer = useDrawer();
  return (
    <div className='fixed w-min inset-x-0 mx-auto z-40 bottom-10'>
      <Button
        onClick={drawer.close}
        type='contained'
        size='lg'
        color={{ classname: 'primary' }}
        icon={{
          iconKey: 'HiOutlineX',
          size: 'base',
          color: { classname: 'white' },
        }}
      />
    </div>
  );
};
